<template>
	<!-- break-it round 3: disable-agree-button below must gate ONLY on isSubmitting (in-flight
		 double-submit protection) — never on form validity. Root CLAUDE.md "Form action buttons —
		 always enabled, no-op when invalid": the button stays enabled and handleSubmit's own validate()
		 guard below no-ops on an invalid form instead of disabling the button. -->
	<Dialog
		:dialog-visible="dialogVisible"
		title="Refer a Friend"
		:agree-text="isSubmitting ? 'Saving...' : 'Save My Referral'"
		cancel-text="Cancel"
		:disable-agree-button="isSubmitting"
		:agree-loading="isSubmitting"
		:options="{ width: '560px' }"
		@cancel="handleCancel"
		@agree="handleSubmit">
		<template #dialog-body>
			<div class="mb-4 text-body-2 text-medium-emphasis">
				Tell us about someone who'd benefit from LiveSwitch. When they become a customer, you get $250!
			</div>
			<v-form ref="referralForm" v-model="isFormValid">
				<v-row dense>
					<v-col cols="12" sm="6">
						<CharacterLimitedTextField
							v-test="'refer-a-friend-first-name'"
							v-model.trim="form.firstName"
							field-noun="first name"
							label="First name (Required)"
							placeholder="Jane"
							:max-length="FIELD_LENGTHS.NAME"
							:rules="firstNameRules"
							validate-on="invalid-input" />
					</v-col>
					<v-col cols="12" sm="6">
						<CharacterLimitedTextField
							v-test="'refer-a-friend-last-name'"
							v-model.trim="form.lastName"
							field-noun="last name"
							label="Last name (Required)"
							placeholder="Doe"
							:max-length="FIELD_LENGTHS.NAME"
							:rules="lastNameRules"
							validate-on="invalid-input" />
					</v-col>
				</v-row>
				<EmailTextField
					v-test="'refer-a-friend-email'"
					v-model.trim="form.email"
					label="Email address (Required)"
					placeholder="jane@company.com"
					:max-length="FIELD_LENGTHS.EMAIL"
					:rules="emailRules"
					validate-on="invalid-input" />
				<CharacterLimitedTextField
					v-test="'refer-a-friend-company-name'"
					v-model.trim="form.companyName"
					field-noun="company name"
					label="Company name (Required)"
					placeholder="Acme Inc."
					:max-length="COMPANY_NAME_MAX_LENGTH"
					:rules="companyNameRules"
					validate-on="invalid-input" />
			</v-form>
		</template>
	</Dialog>
</template>

<script>
	import Dialog from "./Dialog.vue";
	import api from "../api";
	import { VuetifyValidation, FIELD_LENGTHS, validateFormOrWarn } from "@/helpers/Validation";
	import { COMPANY_NAME_MAX_LENGTH } from "@/api/constants";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { useToast } from "vue-toastification";

	const emptyForm = () => ({
		firstName: "",
		lastName: "",
		email: "",
		companyName: ""
	});

	export default {
		name: "ReferAFriendDialog",
		components: { Dialog },
		setup() {
			const toast = useToast();
			return { toast, FIELD_LENGTHS, COMPANY_NAME_MAX_LENGTH };
		},
		props: {
			dialogVisible: {
				type: Boolean,
				default: false
			}
		},
		emits: ["cancel", "referralSent"],
		data() {
			return {
				form: emptyForm(),
				isFormValid: null,
				isSubmitting: false,
				dialogSession: 0
			};
		},
		computed: {
			firstNameRules() {
				return [VuetifyValidation.RequiredValidator("First name").isValid];
			},
			lastNameRules() {
				return [VuetifyValidation.RequiredValidator("Last name").isValid];
			},
			emailRules() {
				return [VuetifyValidation.RequiredValidator("Email address").isValid];
			},
			companyNameRules() {
				return [VuetifyValidation.RequiredValidator("Company name").isValid];
			}
		},
		watch: {
			dialogVisible(visible) {
				if (visible) {
					this.$nextTick(() => this.resetForm());
				}
			}
		},
		methods: {
			handleCancel() {
				this.dialogSession += 1;
				this.resetForm();
				this.$emit("cancel");
			},
			async handleSubmit() {
				if (this.isSubmitting) return;
				this.isSubmitting = true;
				const dialogSession = this.dialogSession;

				// LSP-16852: awaits the real v-form validate() result via the shared validateFormOrWarn
				// helper (guards a malformed/rejected validate() resolution — see its doc comment in
				// Validation.ts) instead of reading a stale validity ref. break-it round 3: this is the
				// only gate on validity now that disable-agree-button (template above) no longer disables
				// the button itself — an invalid submit simply no-ops here.
				try {
					const validationResult = await validateFormOrWarn(
						this.$refs.referralForm,
						"[ReferAFriendDialog] handleSubmit: form ref not mounted, cannot validate"
					);
					if (!validationResult || !validationResult.valid) return;
					if (dialogSession !== this.dialogSession) return;

					const referredFirstName = this.form.firstName;
					try {
						await api.submitReferral({
							firstName: this.form.firstName,
							lastName: this.form.lastName,
							email: this.form.email,
							companyName: this.form.companyName
						});
						if (dialogSession !== this.dialogSession) return;

						const message = `Thanks! We've sent ${referredFirstName} an invite to try LiveSwitch.`;
						this.toast?.success(message, { timeout: calculateTimeout(message) });
						this.$emit("referralSent");
					} catch (error) {
						if (dialogSession !== this.dialogSession) return;
						const errorMessage = getMessageFromError(error) || "We couldn't send the invite right now. Please try again.";
						this.toast?.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
					}
				} finally {
					this.isSubmitting = false;
				}
			},
			resetForm() {
				this.form = emptyForm();
				this.isFormValid = null;
				this.$refs.referralForm?.resetValidation?.();
			}
		}
	};
</script>
